import { BadgeCheck, MapPin, Users, TrendingUp, Briefcase, Eye, Send, Instagram, Youtube, Star, Image } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import StarAvatarFallback from "@/components/StarAvatarFallback";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";

const InfluencerCard = ({ influencer, onClick, onViewProfile, onCollaborate, onViewPortfolio, showActions = false }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    loadImage();
  }, [influencer.picture]);

  const loadImage = async () => {
    if (!influencer.picture) {
      setImageUrl(null);
      return;
    }
    
    if (influencer.picture.startsWith("http")) {
      setImageUrl(influencer.picture);
    } else {
      try {
        const response = await axios.get(`${API}/files/${influencer.picture}`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setImageUrl(blobUrl);
      } catch (error) {
        console.error("Failed to load image:", error);
        setImageUrl(null);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const formatFollowers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count;
  };

  // Handle both string and array for niches
  const getNiches = () => {
    if (Array.isArray(influencer.niches) && influencer.niches.length > 0) {
      return influencer.niches;
    }
    if (influencer.niche) {
      return [influencer.niche];
    }
    return ["Fashion"];
  };

  // Handle both string and array for platforms
  const getPlatforms = () => {
    if (Array.isArray(influencer.platforms) && influencer.platforms.length > 0) {
      return influencer.platforms;
    }
    if (influencer.platform) {
      if (influencer.platform === "Both") {
        return ["Instagram", "YouTube"];
      }
      return [influencer.platform];
    }
    return ["Instagram"];
  };

  const niches = getNiches();
  const platforms = getPlatforms();

  return (
    <div 
      className="influencer-card bg-white rounded-2xl overflow-hidden transition-all hover:shadow-xl hover:scale-[1.02] border border-[#E2E8F0]"
      data-testid={`influencer-card-${influencer.user_id}`}
    >
      {/* Large Profile Photo Section */}
      <div className="relative">
        <div className="h-32 bg-gradient-to-br from-[#FF69B4]/20 via-[#FFF0F5] to-[#87CEEB]/20"></div>
        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
          <div className="relative">
            <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
              <AvatarImage src={imageUrl} alt={influencer.name} className="object-cover" />
              <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                <StarAvatarFallback iconClassName="w-10 h-10" />
              </AvatarFallback>
            </Avatar>
            {influencer.is_verified && (
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center border-2 border-white shadow-md verified-badge">
                <BadgeCheck className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="pt-14 pb-4 px-4 text-center">
        <h3 className="font-outfit font-bold text-lg text-[#1E293B] mb-2">{influencer.name}</h3>
        
        {/* Multiple Niches */}
        <div className="flex flex-wrap items-center justify-center gap-1 mb-2">
          {niches.slice(0, 3).map((niche, idx) => (
            <Badge 
              key={idx} 
              className="bg-[#FFF0F5] text-[#FF69B4] hover:bg-[#FFF0F5] font-medium text-xs"
            >
              {niche}
            </Badge>
          ))}
          {niches.length > 3 && (
            <Badge className="bg-[#F0F9FF] text-[#87CEEB] hover:bg-[#F0F9FF] text-xs">
              +{niches.length - 3}
            </Badge>
          )}
        </div>

        {/* Location - Show most precise available */}
        <div className="flex items-center justify-center gap-1 mb-2">
          <MapPin className="w-3 h-3 text-[#FF69B4]" />
          <span className="text-sm text-[#64748B] font-medium">
            {influencer.village 
              ? `${influencer.village}${influencer.district ? `, ${influencer.district}` : ''}`
              : influencer.city && influencer.state 
                ? `${influencer.city}, ${influencer.state}`
                : influencer.location || "Location not set"}
          </span>
        </div>

        {/* Multiple Platforms */}
        <div className="flex justify-center gap-2 mb-3">
          {platforms.includes("Instagram") && (
            <div className="flex items-center gap-1 text-xs text-pink-500 bg-pink-50 px-2 py-1 rounded-full">
              <Instagram className="w-3 h-3" />
              Instagram
            </div>
          )}
          {platforms.includes("YouTube") && (
            <div className="flex items-center gap-1 text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full">
              <Youtube className="w-3 h-3" />
              YouTube
            </div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-1 px-3 pb-4">
        <div className="bg-[#FFF0F5] rounded-xl p-3 text-center">
          <p className="font-outfit font-bold text-lg text-[#1E293B]">{formatFollowers(influencer.followers_count)}</p>
          <p className="text-xs text-[#64748B]">Followers</p>
        </div>
        <div className="bg-[#FFF7ED] rounded-xl p-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <p className="font-outfit font-bold text-lg text-[#1E293B]">{influencer.trust_score || "New"}</p>
          </div>
          <p className="text-xs text-[#64748B]">Trust</p>
        </div>
        <div className="bg-[#F0F9FF] rounded-xl p-3 text-center">
          <p className="font-outfit font-bold text-lg text-[#1E293B]">{influencer.completed_deals || 0}</p>
          <p className="text-xs text-[#64748B]">Deals</p>
        </div>
      </div>

      {/* Rating Display */}
      {influencer.avg_rating && (
        <div className="px-3 pb-3">
          <div className="flex items-center justify-center gap-1 text-sm text-[#64748B]">
            <span>{'⭐'.repeat(Math.round(influencer.avg_rating))}</span>
            <span>{influencer.avg_rating}/5</span>
            <span className="text-xs">({influencer.total_ratings} reviews)</span>
          </div>
        </div>
      )}

      {/* Action Buttons for Brands */}
      {showActions && (
        <div className="grid grid-cols-3 gap-2 px-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewProfile && onViewProfile(influencer);
            }}
            className="rounded-full text-[#64748B] hover:text-[#1E293B] hover:border-[#87CEEB] hover:bg-[#F0F9FF]"
            data-testid={`view-profile-btn-${influencer.user_id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewPortfolio && onViewPortfolio(influencer);
            }}
            className="rounded-full text-[#64748B] hover:text-[#1E293B] hover:border-purple-400 hover:bg-purple-50"
            data-testid={`portfolio-btn-${influencer.user_id}`}
          >
            <Image className="w-4 h-4 mr-1" />
            Work
          </Button>
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCollaborate && onCollaborate(influencer);
            }}
            className="rounded-full bg-[#FF69B4] hover:bg-[#E05297] text-white"
            data-testid={`collab-btn-${influencer.user_id}`}
          >
            <Send className="w-4 h-4 mr-1" />
            Collab
          </Button>
        </div>
      )}
    </div>
  );
};

export default InfluencerCard;
